import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ImageCropSavePage from './components/ImageSave';
import ImageSearchSelectMember from './components/ImageSearchSelectMember';
import type { ImageSearchResultType } from './types/type';
import ImageSearchResultContainer from './components/ImageSearchResult/ImageSearchResultContainer';
import { t } from 'i18next';
import { setEvent } from '@pocapan/shared/utils/ga';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';

const STEPS = {
  SAVE_IMAGE: 'SAVE_IMAGE',
  SELECT_MEMBER: 'SELECT_MEMBER',
  SEARCH_RESULT: 'SEARCH_RESULT'
};

const ImageSearchPage = () => {
  const navigate = useNavigate();
  const handleHistoryBack = useHandleHistoryBack();
  const [searchParams] = useSearchParams();
  const origin = searchParams.get('origin');
  const mode = searchParams.get('mode') ?? undefined;
  const originForSelectMethod = origin === 'select-method';
  const isSellMode = mode === 'sell';
  const isExchangeMode = mode === 'exchange';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingMemberRef = useRef<ImageSearchResultType['selectedMember'] | null>(null);

  const [step, setStep] = useState(
    isSellMode || isExchangeMode ? STEPS.SELECT_MEMBER : STEPS.SAVE_IMAGE
  );
  const [imageData, setImageData] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<ImageSearchResultType['selectedMember']>({
    artistId: '',
    artistName: '',
    memberId: '',
    memberName: ''
  });

  // GA 이벤트 전송 함수
  const sendGAEvent = useCallback((action: string, label?: string, value?: number) => {
    setEvent({
      category: 'Image Search',
      action: action,
      label: label,
      value: value
    });
  }, []);

  // step 변경 시 GA 이벤트 전송
  useEffect(() => {
    switch (step) {
      case STEPS.SAVE_IMAGE:
        sendGAEvent('view_image_upload', '이미지 업로드 화면 진입');
        break;
      case STEPS.SELECT_MEMBER:
        sendGAEvent('view_member_selection', '멤버 선택 화면 진입');
        break;
      case STEPS.SEARCH_RESULT:
        sendGAEvent('view_search_result', '검색 결과 화면 진입');
        break;
    }
  }, [step, sendGAEvent]);

  const beforeUploadContent = {
    imageSrc: originForSelectMethod
      ? '/assets/icons/image-search/image_search.png'
      : '/assets/icons/image-search/image_save_content.png',
    title: originForSelectMethod
      ? t('imageSearch.beforeUpload.titleForSelectMethod')
      : t('imageSearch.beforeUpload.title'),
    description: originForSelectMethod
      ? t('imageSearch.beforeUpload.descriptionForSelectMethod')
      : t('imageSearch.beforeUpload.description'),
    disclaimerRowLine: [
      t('imageSearch.beforeUpload.disclaimerRowLine1'),
      t('imageSearch.beforeUpload.disclaimerRowLine2'),
      t('imageSearch.beforeUpload.disclaimerRowLine3')
    ],
    bottomDescription: t('imageSearch.beforeUpload.bottomDescription')
  };

  const onMemberSelect = ({
    artistId,
    artistName,
    memberId,
    memberName
  }: {
    artistId: string;
    artistName: string;
    memberId: string;
    memberName: string;
  }) => {
    sendGAEvent('select_member', `${artistName} - ${memberName}`, 1);
    if (isSellMode || isExchangeMode) {
      pendingMemberRef.current = { artistId, artistName, memberId, memberName };
      fileInputRef.current?.click();
    } else {
      setSelectedMember({ artistId, artistName, memberId, memberName });
      setStep(STEPS.SEARCH_RESULT);
    }
  };

  const handleSellModeFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingMemberRef.current) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataURL = ev.target?.result as string;
      setSelectedMember(pendingMemberRef.current!);
      setImageData(dataURL);
      sendGAEvent('complete_image_upload', '이미지 업로드 완료', 1);
      setStep(STEPS.SAVE_IMAGE);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleSellModeFileSelect}
      />
      {step === STEPS.SAVE_IMAGE && (
        <ImageCropSavePage
          setImageData={setImageData}
          initialImageData={isSellMode || isExchangeMode ? (imageData ?? undefined) : undefined}
          onComplete={() => {
            sendGAEvent('complete_image_upload', '이미지 업로드 완료', 1);
            setStep(isSellMode || isExchangeMode ? STEPS.SEARCH_RESULT : STEPS.SELECT_MEMBER);
          }}
          onCancel={() => {
            setImageData(null);
            if (isSellMode || isExchangeMode) {
              setStep(STEPS.SELECT_MEMBER);
            } else {
              handleHistoryBack();
            }
          }}
          beforeUploadContent={beforeUploadContent}
        />
      )}
      {step === STEPS.SELECT_MEMBER && (
        <ImageSearchSelectMember
          showCameraButton={isSellMode || isExchangeMode}
          mode={mode}
          onClose={() => {
            sendGAEvent('back_from_member_selection', '멤버 선택 화면에서 뒤로가기', 1);
            if (isSellMode || isExchangeMode) {
              navigate(-1);
            } else {
              setStep(STEPS.SAVE_IMAGE);
            }
          }}
          onMemberSelect={onMemberSelect}
        />
      )}
      {step === STEPS.SEARCH_RESULT && imageData && (
        <ImageSearchResultContainer
          imageData={imageData}
          selectedMember={selectedMember}
          mode={mode}
          onBackClick={() => {
            sendGAEvent('back_from_search_result', '검색 결과 화면에서 뒤로가기', 1);
            setStep(isSellMode || isExchangeMode ? STEPS.SAVE_IMAGE : STEPS.SELECT_MEMBER);
          }}
          onAnotherSearchClick={() => {
            sendGAEvent('failed_another_search', '실패 시 다른 검색하기 클릭', 1);
            setStep(STEPS.SAVE_IMAGE);
            setImageData(null);
          }}
        />
      )}
    </div>
  );
};

export default ImageSearchPage;
